import React from "react";
import * as yup from "yup";
import WizardFormStep from "../../components/WizardFormStep";
import { Row, Col } from "reactstrap";
import { useFormContext } from "react-hook-form";
import withStyles from "isomorphic-style-loader/withStyles";
import s from "./styles.scss";
import FormInput from "../../../../../../toolkit/formInput";
import { Radio, RadioGroup } from "@mui/material";
import { createDomainField, extractDomain } from "../../../../../../utils/validation/validationUtils";
import WizardFooter from "../../../../../../toolkit/wizardFooter";
import { serviceCaller, ServiceCallerProps } from "../../../../../composition";
import { saveBrandSetupData } from "../../../../../../utils/firebase/brandSetup";
import { useBrandSetupWizardContext } from "../context";

export const brandDetailsFormSchema = yup.object({
  brandSetupMethod: yup.string().required("Please select a setup method"),
  brandUrl: yup.string().when("brandSetupMethod", {
    is: "fetch",
    then: () => createDomainField("Website URL")
      .transform((value) => extractDomain(value)),
    otherwise: schema => schema.nullable(),
  }),
});

interface BrandDetailsFormProps extends ServiceCallerProps {}

const BrandDetailsForm: React.FC<BrandDetailsFormProps> = ({
  currentCharity,
}) => {
  const { setValue, watch } = useFormContext();
  const setupMethod = watch("brandSetupMethod") || "";
  const { customizeStepIndex } = useBrandSetupWizardContext();

  const customNextStep =
    setupMethod === "manual" && customizeStepIndex >= 0
      ? customizeStepIndex
      : undefined;

  const handleMethodChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const method = event.target.value;
    setValue("brandSetupMethod", method);
    if (method === "manual") {
      setValue("brandUrl", "");
    }
  };

  const handleNext = async (formData: any) => {
    if (!currentCharity?.id) {
      console.error("No charity ID available");
      return;
    }

    try {
      const cleanBrandUrl = formData.brandUrl ? extractDomain(formData.brandUrl) : null;
      await saveBrandSetupData(currentCharity.id, {
        setupMethod: formData.brandSetupMethod,
        brandUrl: cleanBrandUrl,
        progressTracker: "brand_added",
      });
    } catch (error) {
      console.error("Failed to save brand setup data:", error);
      throw error;
    }
  };

  return (
    <WizardFormStep
      title="Set Up Your Branding"
      subtitle="Insert link to the following required information"
    >
      <Row className={`justify-content-center mt-2`}>
        <Col md={7} className={``}>
          <RadioGroup value={setupMethod} onChange={handleMethodChange}>
            <div className="mb-4">
              <Row className="align-items-start">
                <Col xs="auto" className="pt-2 pe-1">
                  <Radio
                    value="fetch"
                    checked={setupMethod === "fetch"}
                    onChange={handleMethodChange}
                    size="small"
                  />
                </Col>
                <Col>
                  <FormInput
                    name="brandUrl"
                    type="text"
                    icon={"fa-solid fa-share-nodes"}
                    placeholder="Enter your website URL"
                    disabled={setupMethod !== "fetch"}
                  />
                  <p className={s.helperText}>
                    We’ll fetch your brand assets automatically.
                  </p>
                </Col>
              </Row>
            </div>

            <Row className="align-items-center">
              <Col xs="auto" className="pe-1">
                <Radio
                  value="manual"
                  checked={setupMethod === "manual"}
                  onChange={handleMethodChange}
                  size="small"
                />
              </Col>
              <Col>
                <span className={s.optionLabel}>Upload files manually</span>
                <p className={s.helperText}>
                  Drag & drop your logo, favicon, and any other files in the next step.
                </p>
              </Col>
            </Row>
          </RadioGroup>
        </Col>
      </Row>

      <WizardFooter
        schema={brandDetailsFormSchema}
        onNext={handleNext}
        customNextStep={customNextStep}
      />
    </WizardFormStep>
  );
};

export default serviceCaller(withStyles(s)(BrandDetailsForm));
